import type { ReactNode } from "react";
import { cn } from "@/lib/utils";

export type Chip<V extends string> = { value: V; label: ReactNode; count?: number };

/**
 * The app's one kind of switch: a pill tray of chips with the active one filled
 * in the brand colour. Used for every filter and every either/or choice, the
 * period included, so they all look and behave the same.
 */
export function ChipGroup<V extends string>({
  options,
  value,
  onValueChange,
  label,
  fullWidth = false,
  className,
}: {
  options: Chip<V>[];
  value: V | null;
  onValueChange: (value: V) => void;
  /** Accessible name of the group. */
  label: string;
  fullWidth?: boolean;
  className?: string;
}) {
  return (
    <div
      role="radiogroup"
      aria-label={label}
      className={cn(
        "bg-secondary flex flex-wrap items-center gap-0.5 rounded-full p-0.5",
        fullWidth && "w-full flex-nowrap",
        className,
      )}
    >
      {options.map((o) => {
        const active = o.value === value;
        return (
          <button
            key={o.value}
            type="button"
            role="radio"
            aria-checked={active}
            onClick={() => onValueChange(o.value)}
            className={cn(
              "inline-flex h-7 items-center justify-center gap-1.5 rounded-full px-3 text-xs font-medium whitespace-nowrap transition-colors",
              "focus-visible:ring-ring/50 outline-none focus-visible:ring-2",
              fullWidth && "flex-1",
              active
                ? "bg-primary text-primary-foreground"
                : "text-muted-foreground hover:bg-accent hover:text-foreground",
            )}
          >
            {o.label}
            {o.count !== undefined && (
              <span
                className={cn(
                  "inline-flex h-4 min-w-4 items-center justify-center rounded-full px-1 text-[10px] tabular-nums",
                  active ? "bg-primary-foreground/20" : "bg-foreground/10",
                )}
              >
                {o.count}
              </span>
            )}
          </button>
        );
      })}
    </div>
  );
}
